// Course-level turf health score with a 30-day sparkline. Composite NDVI
// rescaled to 0–100 across the (demo-filtered) zone set. Zone detail pages
// retain the six-index diagnostic view — this is the pitch tile.

import { useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus, Activity } from 'lucide-react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from 'recharts';
import { DataCard } from './ui/data-card';
import { getCourseHealth, type CourseHealthResponse } from '../api/overview.js';
import {
  DARK_CHART_TOKENS,
  darkTooltipContentStyle,
  darkTooltipItemStyle,
  darkTooltipLabelStyle,
} from '../lib/charts-theme';

interface Props {
  courseSlug: string;
}

function scoreBand(score: number): { label: string; color: string } {
  if (score >= 80) return { label: 'Strong', color: DARK_CHART_TOKENS.greenPrimary };
  if (score >= 60) return { label: 'Healthy', color: DARK_CHART_TOKENS.greenBright };
  if (score >= 40) return { label: 'Watch', color: DARK_CHART_TOKENS.orange };
  return { label: 'Stressed', color: DARK_CHART_TOKENS.red };
}

export function CourseHealthCard({ courseSlug }: Props) {
  const [data, setData] = useState<CourseHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getCourseHealth(courseSlug, 30);
        if (!cancelled) setData(resp);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) {
    return (
      <DataCard title="Course health" className="mt-6">
        <div className="text-xs text-fg-muted">{error}</div>
      </DataCard>
    );
  }
  if (!data) {
    return (
      <DataCard title="Course health" className="mt-6">
        <div className="text-sm text-fg-muted">Loading…</div>
      </DataCard>
    );
  }

  const score = data.score;
  const band = score != null ? scoreBand(score) : null;
  const delta = data.delta7d;
  const DeltaIcon =
    delta == null || delta === 0 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
  const deltaColor =
    delta == null || delta === 0
      ? DARK_CHART_TOKENS.axisColor
      : delta > 0
        ? DARK_CHART_TOKENS.greenPrimary
        : DARK_CHART_TOKENS.red;

  return (
    <DataCard
      title="Course health"
      subtitle="Composite NDVI rescaled 0–100 across visible zones · 30-day window"
      actions={
        <div className="flex items-center gap-1 text-xs text-fg-muted">
          <Activity size={12} />
          {data.sampleCount} obs
        </div>
      }
    >
      <div className="flex items-end justify-between gap-6">
        <div>
          {score == null ? (
            <div className="text-sm text-fg-muted">
              No satellite observations yet. Run /cron/satellite-refresh to seed.
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <div
                  className="font-display text-5xl font-semibold leading-none"
                  style={{ color: band?.color }}
                >
                  {score}
                </div>
                <div className="text-xs uppercase tracking-wide text-fg-muted">/ 100</div>
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs">
                <span style={{ color: band?.color }}>{band?.label}</span>
                {delta != null && (
                  <span className="flex items-center gap-0.5" style={{ color: deltaColor }}>
                    <DeltaIcon size={12} />
                    {delta === 0 ? 'flat' : `${delta > 0 ? '+' : ''}${delta} vs 7d ago`}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {data.sparkline.length >= 2 && (
          <div className="h-20 w-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.sparkline} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="healthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={DARK_CHART_TOKENS.greenPrimary} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={DARK_CHART_TOKENS.greenPrimary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={[0, 100]} hide />
                <Tooltip
                  contentStyle={darkTooltipContentStyle}
                  itemStyle={darkTooltipItemStyle}
                  labelStyle={darkTooltipLabelStyle}
                  formatter={(v) => [`${v} / 100`, 'Health']}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke={DARK_CHART_TOKENS.greenPrimary}
                  fill="url(#healthGradient)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </DataCard>
  );
}
